import { Box } from '@mui/material'
import { FormattedMessage, useIntl } from 'react-intl'
import type { Session } from '@ory/client'

import { tokens } from '@configuration/theme'
import Button from '@base/Button/Button.component'
import InlineMessage from '@base/InlineMessage/InlineMessage.component'
import SplitAuthLayout from '@components/SplitAuthLayout/SplitAuthLayout.component'
import { useSignOut } from '@hooks/useSignOut'
import { useIdentityLocale } from '@i18n/hooks/useIdentityLocale'
import { isTrustedReturnTo, defaultReturnUrl } from '@utils/url.utils'

interface Props {
  session: Session
  returnTo: string | null
}

const AlreadySignedInPanel = ({ session, returnTo }: Props) => {
  const intl = useIntl()
  const { signOut, error: signOutError, pending } = useSignOut()

  useIdentityLocale({ identity: session.identity, source: 'identity-trait' })

  // `||` not `??` — an empty-string `traits.email` (theoretically possible if
  // Kratos's schema check ever regresses) would otherwise render an empty
  // `<strong></strong>` in the body, which reads weirder than the fallback.

  const identifier =
    session.identity?.traits?.email ||
    intl.formatMessage({ id: 'login.alreadySignedIn.fallbackIdentifier' })
  // `returnTo` here is rendered directly as an <a href> (not laundered through
  // Kratos), so an unchecked value is an open redirect. Restrict to trusted
  // origins and fall back to the product URL otherwise.
  const continueHref = returnTo && isTrustedReturnTo(returnTo) ? returnTo : defaultReturnUrl()

  return (
    <SplitAuthLayout
      heading={intl.formatMessage({ id: 'login.alreadySignedIn.welcome' })}
      dataTestId='already-signed-in-panel'
      showLanguageSwitcher={false}
    >
      <Box sx={{ color: tokens.businessBlack60, fontSize: 14, lineHeight: 1.4 }}>
        <FormattedMessage
          id='login.alreadySignedIn.body'
          values={{
            identifier,
            strong: (chunks) => <strong>{chunks}</strong>,
          }}
        />
      </Box>

      {signOutError && <InlineMessage variant='error'>{signOutError}</InlineMessage>}

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <Button variant='primary' size='lg' block={true} component='a' href={continueHref}>
          <FormattedMessage id='login.alreadySignedIn.continue' />
        </Button>
        <Button
          variant='bordered'
          size='lg'
          block={true}
          type='button'
          onClick={signOut}
          disabled={pending}
        >
          {intl.formatMessage({
            id: pending ? 'common.signOut.pending' : 'common.signOut.label',
          })}
        </Button>
      </Box>
    </SplitAuthLayout>
  )
}

export default AlreadySignedInPanel
